// /chord-craft/gym — the Voicing Gym: two hands, from anchor to rootless.
// Learn tab: pick a Palette chord + a voicing level (1 Anchor → 4 Rootless)
// and see/hear how voiceChord splits it across the hands, with extension
// tones glowing fuchsia against the amber (left hand) / sky (right hand)
// skeleton. Drill tab: two drill modes — "Shuffle" (default), random chord
// prompts by pool (triads+7ths / +9ths / everything) at the selected level;
// "Around the circle", a locked chord color + level walked through KEYS in
// circle-of-fifths order, one prompt per key, wrapping with a lap counter.
// Play the exact voicing (MIDI or the on-screen CraftPiano) to advance, or
// flip on "loose mode" to be judged on pitch classes only. While you hold
// notes that don't belong to the target, those keys light up 'wrong' (rose)
// on the piano; a failed check also summarizes right/wrong/missing. Hint
// reveals the target roles on the piano. Streak, attempts, and the drill
// mode choice persist to localStorage under `chordCraft:gym:v1`.
//
// House pattern: Learn/Drill sub-tabs + localStorage stats copied from
// /circle-of-fifths (src/pages/CircleOfFifths.js). Theory comes from Agent
// A's src/lib/chordCraft; chrome/piano come from Agent B's
// src/components/chordCraft/{CraftShell,CraftPiano}. Input plumbing (Web
// MIDI) is src/components/synthlab/useSynthInput's useMidiInput; playback is
// src/lib/homeKeyAudio's playNote(PATCHES.softKeys, midi, durMs, whenMs).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CraftShell from "../components/chordCraft/CraftShell";
import CraftPiano from "../components/chordCraft/CraftPiano";
import {
  BASES,
  SEVENTHS,
  EXTENSIONS,
  chordPcs,
  chordSymbol,
  specFromPreset,
  PALETTE,
  VOICING_LEVELS,
  voiceChord,
  matchesChord,
  matchesVoicing,
} from "../lib/chordCraft";
import { KEYS, keyByName } from "../lib/pianoDrills";
import { PATCHES, playNote } from "../lib/homeKeyAudio";
import { useMidiInput } from "../components/synthlab/useSynthInput";
import { cn } from "../lib/utils";

// Covers voiceChord's default hand ranges (lhLow 40 – rhHigh 84) with a
// little headroom on the low end so LH root-position anchors never clip.
const LOW = 40;
const HIGH = 84;

const GYM_STATS_KEY = "chordCraft:gym:v1";

const POOLS = [
  { id: "triads7", label: "Triads + 7ths" },
  { id: "ninths", label: "+ 9ths" },
  { id: "everything", label: "Everything" },
];

const TABS = [
  { id: "learn", label: "Learn" },
  { id: "drill", label: "Drill" },
];

// Drill sub-modes: "shuffle" is today's random-pool prompt; "circle" locks a
// chord color + level and walks KEYS (circle-of-fifths order) one root per
// prompt, wrapping back to C with a lap counter.
const DRILL_MODES = [
  { id: "shuffle", label: "Shuffle" },
  { id: "circle", label: "Around the circle" },
];

const pc = (midi) => ((midi % 12) + 12) % 12;

const EXT_SEMIS = EXTENSIONS.reduce((m, e) => {
  m[e.id] = e.semis;
  return m;
}, {});

const BASE_IDS = BASES.map((b) => b.id);
const SEVENTH_IDS = SEVENTHS.map((s) => s.id);
const EXTENSION_IDS = EXTENSIONS.map((e) => e.id);
const NINTH_IDS = EXTENSION_IDS.filter((id) => id === "9" || id === "b9" || id === "#9");

const randomPick = (arr, rng) => arr[Math.floor(rng() * arr.length)];

// A chord spec in the shape voiceChord/chordSymbol/matchesChord expect:
// { rootPc, base, seventh, extensions }. Pool controls how colorful it gets.
const randomSpec = (poolId, rng = Math.random) => {
  const rootPc = Math.floor(rng() * 12);
  const base = randomPick(BASE_IDS, rng);
  let seventh = null;
  let extensions = [];

  if (poolId === "ninths") {
    if (rng() < 0.7) seventh = randomPick(SEVENTH_IDS, rng);
    if (rng() < 0.85) extensions = [randomPick(NINTH_IDS, rng)];
  } else if (poolId === "everything") {
    if (rng() < 0.85) seventh = randomPick(SEVENTH_IDS, rng);
    const count = 1 + Math.floor(rng() * 2);
    const pool = [...EXTENSION_IDS];
    for (let i = 0; i < count && pool.length; i += 1) {
      extensions.push(pool.splice(Math.floor(rng() * pool.length), 1)[0]);
    }
  } else {
    // triads7: plain triads most of the time, a seventh sometimes.
    if (rng() < 0.55) seventh = randomPick(SEVENTH_IDS, rng);
  }

  return { rootPc, base, seventh, extensions };
};

// Which pitch classes in `spec` are color tones (extensions) vs skeleton —
// used to split a voiceChord {left,right} result into left/right/color roles
// for CraftPiano.
const buildRoles = (spec, voicing) => {
  const extPcs = new Set(
    (spec.extensions || []).map((id) => (spec.rootPc + (EXT_SEMIS[id] || 0)) % 12)
  );
  const roles = {};
  (voicing.left || []).forEach((m) => {
    roles[m] = extPcs.has(pc(m)) ? "color" : "left";
  });
  (voicing.right || []).forEach((m) => {
    roles[m] = extPcs.has(pc(m)) ? "color" : "right";
  });
  return roles;
};

const playHand = (notes) => {
  if (!notes || !notes.length) return;
  notes.forEach((m, i) => playNote(PATCHES.softKeys, m, 900, i * 12));
};

/* ---------- localStorage stats ---------- */
// This blob is also the Gym's only prefs store — drillMode (Shuffle vs.
// Around the circle) rides along in the same object so it survives reloads
// without a second localStorage key.

const emptyGymStats = () => ({ attempts: 0, correct: 0, streak: 0, best: 0, drillMode: "shuffle" });

const loadGymStats = () => {
  try {
    const raw = window.localStorage.getItem(GYM_STATS_KEY);
    if (!raw) return emptyGymStats();
    const parsed = JSON.parse(raw);
    return { ...emptyGymStats(), ...parsed };
  } catch {
    return emptyGymStats();
  }
};

const saveGymStats = (stats) => {
  try {
    window.localStorage.setItem(GYM_STATS_KEY, JSON.stringify(stats));
  } catch {
    // private mode / quota — stats just don't persist
  }
};

const recordGymResult = (stats, correct) => {
  const prev = stats || emptyGymStats();
  const streak = correct ? prev.streak + 1 : 0;
  return {
    ...prev, // keep prefs (drillMode) riding along untouched
    attempts: prev.attempts + 1,
    correct: prev.correct + (correct ? 1 : 0),
    streak,
    best: Math.max(prev.best, streak),
  };
};

const Chip = ({ active, onClick, children, accent = true }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "rounded-full border px-3 py-1 text-sm transition-colors",
      active
        ? accent
          ? "border-amber-400 bg-amber-400 text-stone-950 font-medium"
          : "border-amber-400/60 bg-amber-400/10 text-amber-200"
        : "border-stone-800 bg-stone-800 text-stone-300 hover:border-amber-400/40"
    )}
  >
    {children}
  </button>
);

const Panel = ({ className, children }) => (
  <div className={cn("rounded-2xl border border-stone-800 bg-stone-900/80 p-4", className)}>
    {children}
  </div>
);

export default function ChordCraftGym() {
  const [tab, setTab] = useState("learn");

  // Shared input: one activeMidis Set fed by MIDI + the on-screen CraftPiano.
  const [activeMidis, setActiveMidis] = useState(() => new Set());
  const heldRef = useRef(new Set());
  const wrongRecordedRef = useRef(false);
  const timeoutsRef = useRef([]);
  const later = useCallback((fn, ms) => {
    timeoutsRef.current.push(setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timeoutsRef.current.forEach(clearTimeout), []);

  /* ---------- Learn tab state ---------- */

  const [learnKeyName, setLearnKeyName] = useState("C");
  const [learnPaletteId, setLearnPaletteId] = useState(
    (PALETTE[0] && PALETTE[0].id) || "maj7"
  );
  const [learnLevel, setLearnLevel] = useState(1);

  const learnKey = keyByName(learnKeyName) || KEYS[0];
  const learnPaletteEntry = PALETTE.find((p) => p.id === learnPaletteId) || PALETTE[0];
  const learnSpec = useMemo(
    () => specFromPreset(learnPaletteId, learnKey.tonicPc),
    [learnPaletteId, learnKey]
  );
  const learnVoicing = useMemo(
    () => voiceChord(learnSpec, learnLevel),
    [learnSpec, learnLevel]
  );
  const learnRoles = useMemo(() => buildRoles(learnSpec, learnVoicing), [learnSpec, learnVoicing]);
  const learnSymbol = chordSymbol(learnSpec, learnKey.useFlats);
  const learnLevelInfo = VOICING_LEVELS.find((l) => l.id === learnLevel) || VOICING_LEVELS[0];

  /* ---------- Drill tab state ---------- */

  const [pool, setPool] = useState("triads7");
  const [drillLevel, setDrillLevel] = useState(1);
  const [looseMode, setLooseMode] = useState(false);
  const [drillSpec, setDrillSpec] = useState(() => randomSpec("triads7")); // shuffle mode's current prompt
  const [status, setStatus] = useState(null); // null | 'correct' | 'wrong'
  const [hintOn, setHintOn] = useState(false);
  const [stats, setStats] = useState(() => loadGymStats());

  // Circle mode: the locked chord color plus a walk position (index into
  // KEYS) and lap count. Not randomized — circleSpec below is purely
  // derived from these two, so "advancing" just steps the index.
  const [circlePaletteId, setCirclePaletteId] = useState(
    (PALETTE[0] && PALETTE[0].id) || "maj7"
  );
  const [circleIndex, setCircleIndex] = useState(0);
  const [circleLap, setCircleLap] = useState(1);
  const [lapFlash, setLapFlash] = useState(false); // brief flash on lap wrap

  const drillMode = stats.drillMode === "circle" ? "circle" : "shuffle";
  const setDrillMode = useCallback((mode) => {
    setStats((prev) => {
      const next = { ...prev, drillMode: mode };
      saveGymStats(next);
      return next;
    });
  }, []);

  const circleKey = KEYS[circleIndex] || KEYS[0];
  const circleSpec = useMemo(
    () => specFromPreset(circlePaletteId, circleKey.tonicPc),
    [circlePaletteId, circleKey]
  );
  // Locking a new chord color starts the walk over at C, lap 1.
  useEffect(() => {
    setCircleIndex(0);
    setCircleLap(1);
    setLapFlash(false);
  }, [circlePaletteId]);

  const currentDrillSpec = drillMode === "circle" ? circleSpec : drillSpec;
  const drillUseFlats = drillMode === "circle" ? circleKey.useFlats : false;

  const drillVoicing = useMemo(
    () => voiceChord(currentDrillSpec, drillLevel),
    [currentDrillSpec, drillLevel]
  );
  const drillRoles = useMemo(
    () => buildRoles(currentDrillSpec, drillVoicing),
    [currentDrillSpec, drillVoicing]
  );
  const drillSymbol = chordSymbol(currentDrillSpec, drillUseFlats);
  const drillLevelInfo = VOICING_LEVELS.find((l) => l.id === drillLevel) || VOICING_LEVELS[0];

  const resetPromptState = useCallback(() => {
    setStatus(null);
    setHintOn(false);
    wrongRecordedRef.current = false;
  }, []);

  // Advance to the next drill prompt: a fresh random chord in shuffle mode,
  // or one step around the circle-of-fifths walk in circle mode (wrapping
  // back to index 0 bumps the lap counter and triggers a brief flash).
  const advanceDrill = useCallback(() => {
    if (drillMode === "circle") {
      setCircleIndex((i) => {
        const next = (i + 1) % KEYS.length;
        if (next === 0) {
          setCircleLap((lap) => lap + 1);
          setLapFlash(true);
          later(() => setLapFlash(false), 1100);
        }
        return next;
      });
    } else {
      setDrillSpec(randomSpec(pool));
    }
    resetPromptState();
  }, [drillMode, pool, later, resetPromptState]);

  // A new pool, level, or drill mode invalidates the current attempt —
  // reroll (shuffle mode only; circle mode keeps its walk position) and
  // clear status/hint.
  useEffect(() => {
    if (drillMode === "shuffle") {
      setDrillSpec(randomSpec(pool));
    }
    resetPromptState();
  }, [pool, drillLevel, drillMode, resetPromptState]);

  // Switching tabs clears held notes / drill status so nothing carries over.
  useEffect(() => {
    heldRef.current = new Set();
    setActiveMidis(new Set());
    setStatus(null);
    wrongRecordedRef.current = false;
  }, [tab]);

  const bumpStats = useCallback((correct) => {
    setStats((prev) => {
      const next = recordGymResult(prev, correct);
      saveGymStats(next);
      return next;
    });
  }, []);

  const judge = useCallback(
    (heldMidis) => {
      if (status === "correct") return;
      const total = (drillVoicing.left || []).length + (drillVoicing.right || []).length;
      const minNeeded = looseMode ? new Set(chordPcs(currentDrillSpec)).size : total;
      if (heldMidis.length === 0) return;
      const ok = looseMode
        ? matchesChord(heldMidis, currentDrillSpec)
        : matchesVoicing(heldMidis, drillVoicing, 0);
      if (ok) {
        setStatus("correct");
        bumpStats(true);
        later(() => advanceDrill(), 900);
        return;
      }
      if (heldMidis.length >= minNeeded && !wrongRecordedRef.current) {
        wrongRecordedRef.current = true;
        setStatus("wrong");
        bumpStats(false);
      }
    },
    [status, drillVoicing, looseMode, currentDrillSpec, bumpStats, later, advanceDrill]
  );

  // Live wrong-note feedback: while holding notes in the drill that don't
  // belong to the target (strict mode: exact target midis; loose mode:
  // target pitch classes), those held keys get the 'wrong' role — merged
  // into whatever roles the piano is already showing, winning over them.
  // Cleared once the check succeeds (nothing further to flag as wrong).
  const heldWrongMidis = useMemo(() => {
    if (tab !== "drill" || status === "correct") return new Set();
    const targetMidis = new Set([...(drillVoicing.left || []), ...(drillVoicing.right || [])]);
    const targetPcs = new Set(chordPcs(currentDrillSpec));
    const wrong = new Set();
    activeMidis.forEach((m) => {
      const isWrong = looseMode ? !targetPcs.has(pc(m)) : !targetMidis.has(m);
      if (isWrong) wrong.add(m);
    });
    return wrong;
  }, [tab, status, activeMidis, drillVoicing, currentDrillSpec, looseMode]);

  const drillRolesWithWrong = useMemo(() => {
    const base = hintOn ? drillRoles : {};
    if (heldWrongMidis.size === 0) return base;
    const merged = { ...base };
    heldWrongMidis.forEach((m) => {
      merged[m] = "wrong";
    });
    return merged;
  }, [hintOn, drillRoles, heldWrongMidis]);

  // One-line "N right · M wrong · K missing" breakdown shown after a failed
  // check — purely a read of the currently-held notes against the target,
  // recomputed live (status itself doesn't change again until release, per
  // the judge/release logic above, which this does not touch).
  const drillCheckSummary = useMemo(() => {
    if (tab !== "drill" || status !== "wrong") return null;
    if (looseMode) {
      const targetPcs = chordPcs(currentDrillSpec);
      const heldPcs = new Set([...activeMidis].map((m) => pc(m)));
      const right = targetPcs.filter((p) => heldPcs.has(p)).length;
      return { right, wrong: heldWrongMidis.size, missing: targetPcs.length - right };
    }
    const targetMidis = [...(drillVoicing.left || []), ...(drillVoicing.right || [])];
    const right = targetMidis.filter((m) => activeMidis.has(m)).length;
    const missing = targetMidis.filter((m) => !activeMidis.has(m)).length;
    return { right, wrong: heldWrongMidis.size, missing };
  }, [tab, status, looseMode, currentDrillSpec, drillVoicing, activeMidis, heldWrongMidis]);

  /* ---------- input: MIDI + on-screen, one activeMidis Set ---------- */

  const press = useCallback(
    (midi) => {
      if (heldRef.current.has(midi)) return;
      heldRef.current.add(midi);
      setActiveMidis(new Set(heldRef.current));
      playNote(PATCHES.softKeys, midi, 500);
      if (tab === "drill") judge([...heldRef.current]);
    },
    [tab, judge]
  );

  const release = useCallback(
    (midi) => {
      if (!heldRef.current.has(midi)) return;
      heldRef.current.delete(midi);
      setActiveMidis(new Set(heldRef.current));
      if (tab === "drill" && heldRef.current.size === 0 && status !== "correct") {
        setStatus(null);
        wrongRecordedRef.current = false;
      }
    },
    [tab, status]
  );

  const { status: midiStatus, inputs: midiInputs } = useMidiInput(press, release, true);
  const midiReady = midiStatus === "ready" && midiInputs && midiInputs.length > 0;

  return (
    <CraftShell
      room="gym"
      title="Voicing Gym"
      subtitle="Two hands, from anchor to rootless — learn the shapes, then drill them."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex overflow-hidden rounded-full border border-stone-800 bg-stone-900">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                aria-pressed={tab === t.id}
                className={cn(
                  "px-4 py-1.5 text-sm transition-colors",
                  tab === t.id
                    ? "bg-amber-400 text-stone-950 font-medium"
                    : "text-stone-300 hover:bg-stone-800"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <span
            className={cn(
              "ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium",
              midiReady
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                : "border-stone-800 bg-stone-900 text-stone-500"
            )}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", midiReady ? "bg-emerald-400" : "bg-stone-600")} />
            {midiReady ? `MIDI: ${midiInputs[0]}` : "MIDI: none — on-screen keys work too"}
          </span>
        </div>

        {/* min-w-0 on the piano panels: let the wide piano scroll inside its
            panel rather than stretch the single mobile column (grid items
            default to min-width:auto). */}
        {tab === "learn" && (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Panel className="min-w-0">
              <div className="overflow-x-auto">
                <CraftPiano
                  low={LOW}
                  high={HIGH}
                  roles={learnRoles}
                  activeMidis={activeMidis}
                  onPress={press}
                  onRelease={release}
                  useFlats={learnKey.useFlats}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => playHand(learnVoicing.left)}
                  className="rounded-full border border-amber-400/50 bg-amber-400/10 px-3 py-1.5 text-sm text-amber-200 hover:bg-amber-400/20"
                >
                  Hear left hand
                </button>
                <button
                  type="button"
                  onClick={() => playHand(learnVoicing.right)}
                  className="rounded-full border border-sky-400/50 bg-sky-400/10 px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-400/20"
                >
                  Hear right hand
                </button>
                <button
                  type="button"
                  onClick={() => playHand([...(learnVoicing.left || []), ...(learnVoicing.right || [])])}
                  className="rounded-full border border-stone-700 bg-stone-800 px-3 py-1.5 text-sm text-stone-200 hover:border-amber-400/40"
                >
                  Hear both hands
                </button>
              </div>
              <p className="mt-3 text-xs text-stone-500">
                <span className="text-amber-300">amber</span> = left hand ·{" "}
                <span className="text-sky-300">sky</span> = right hand ·{" "}
                <span className="text-fuchsia-300">fuchsia glow</span> = color tones (extensions)
              </p>
            </Panel>

            <div className="space-y-4">
              <Panel className="text-center">
                <p className="font-serif text-4xl text-amber-50">{learnSymbol}</p>
                <p className="mt-1 text-sm text-stone-400">
                  {learnLevelInfo.name} — {learnLevelInfo.blurb}
                </p>
                {learnPaletteEntry && (
                  <p className="mt-2 text-sm italic text-fuchsia-200">{learnPaletteEntry.mood}</p>
                )}
              </Panel>

              <Panel>
                <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Root</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {KEYS.map((k) => (
                    <Chip key={k.name} active={learnKeyName === k.name} onClick={() => setLearnKeyName(k.name)}>
                      {k.name}
                    </Chip>
                  ))}
                </div>
              </Panel>

              <Panel>
                <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Chord</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {PALETTE.map((p) => (
                    <Chip key={p.id} active={learnPaletteId === p.id} onClick={() => setLearnPaletteId(p.id)}>
                      {p.label}
                    </Chip>
                  ))}
                </div>
              </Panel>

              <Panel>
                <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Voicing level</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {VOICING_LEVELS.map((l) => (
                    <Chip key={l.id} active={learnLevel === l.id} onClick={() => setLearnLevel(l.id)}>
                      {l.id}. {l.name}
                    </Chip>
                  ))}
                </div>
              </Panel>
            </div>
          </div>
        )}

        {tab === "drill" && (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Panel
              className={cn(
                "min-w-0 transition-colors",
                status === "correct" && "border-emerald-500/70 bg-emerald-500/10"
              )}
            >
              <div className="overflow-x-auto">
                <CraftPiano
                  low={LOW}
                  high={HIGH}
                  roles={drillRolesWithWrong}
                  activeMidis={activeMidis}
                  onPress={press}
                  onRelease={release}
                  useFlats={drillUseFlats}
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setHintOn((h) => !h)}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm",
                    hintOn
                      ? "border-amber-400 bg-amber-400 text-stone-950 font-medium"
                      : "border-stone-700 bg-stone-800 text-stone-200 hover:border-amber-400/40"
                  )}
                >
                  {hintOn ? "Hide hint" : "Hint"}
                </button>
                <button
                  type="button"
                  onClick={() => playHand([...(drillVoicing.left || []), ...(drillVoicing.right || [])])}
                  className="rounded-full border border-stone-700 bg-stone-800 px-3 py-1.5 text-sm text-stone-200 hover:border-amber-400/40"
                >
                  Hear it
                </button>
                <button
                  type="button"
                  onClick={() => advanceDrill()}
                  className="rounded-full border border-stone-700 bg-stone-800 px-3 py-1.5 text-sm text-stone-200 hover:border-amber-400/40"
                >
                  Skip
                </button>
                {status === "correct" && (
                  <span className="text-sm font-medium text-emerald-400">✓ nice — next one…</span>
                )}
                {status === "wrong" && drillCheckSummary && (
                  <span className="text-sm font-medium text-rose-400">
                    not quite — {drillCheckSummary.right} right · {drillCheckSummary.wrong} wrong ·{" "}
                    {drillCheckSummary.missing} missing
                  </span>
                )}
              </div>
              <p className="mt-3 text-xs text-stone-500">
                <span className="text-amber-300">amber</span> = left hand ·{" "}
                <span className="text-sky-300">sky</span> = right hand ·{" "}
                <span className="text-fuchsia-300">fuchsia glow</span> = color tones (extensions)
              </p>
            </Panel>

            <div className="space-y-4">
              <Panel className="text-center">
                <p className="font-serif text-4xl text-amber-50">{drillSymbol}</p>
                <p className="mt-1 text-sm text-stone-400">
                  {drillLevelInfo.name} — level {drillLevelInfo.id}
                </p>
                {drillMode === "circle" && (
                  <p
                    className={cn(
                      "mt-2 text-sm font-medium",
                      lapFlash ? "text-amber-300" : "text-stone-500"
                    )}
                  >
                    lap {circleLap} · {circleIndex + 1}/{KEYS.length}
                    {lapFlash && " — lap complete! ✦"}
                  </p>
                )}
              </Panel>

              <Panel>
                <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Drill mode</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {DRILL_MODES.map((m) => (
                    <Chip key={m.id} active={drillMode === m.id} onClick={() => setDrillMode(m.id)}>
                      {m.label}
                    </Chip>
                  ))}
                </div>
              </Panel>

              {drillMode === "shuffle" ? (
                <Panel>
                  <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Pool</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {POOLS.map((p) => (
                      <Chip key={p.id} active={pool === p.id} onClick={() => setPool(p.id)}>
                        {p.label}
                      </Chip>
                    ))}
                  </div>
                </Panel>
              ) : (
                <Panel>
                  <p className="text-xs font-medium uppercase tracking-wide text-stone-500">
                    Chord color (locked)
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {PALETTE.map((p) => (
                      <Chip
                        key={p.id}
                        active={circlePaletteId === p.id}
                        onClick={() => setCirclePaletteId(p.id)}
                      >
                        {p.label}
                      </Chip>
                    ))}
                  </div>
                </Panel>
              )}

              <Panel>
                <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Voicing level</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {VOICING_LEVELS.map((l) => (
                    <Chip key={l.id} active={drillLevel === l.id} onClick={() => setDrillLevel(l.id)}>
                      {l.id}. {l.name}
                    </Chip>
                  ))}
                </div>
              </Panel>

              <Panel>
                <label className="flex items-center gap-2 text-sm text-stone-300">
                  <input
                    type="checkbox"
                    checked={looseMode}
                    onChange={(e) => {
                      setLooseMode(e.target.checked);
                      setStatus(null);
                      wrongRecordedRef.current = false;
                    }}
                  />
                  Loose mode — any octave, any voicing (checks notes, not exact keys)
                </label>
              </Panel>

              <Panel>
                <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Stats</p>
                <p className="mt-1.5 text-sm text-stone-300">
                  {stats.correct}/{stats.attempts} correct · streak {stats.streak}
                  {stats.best > 0 && ` · best ${stats.best}`}
                </p>
              </Panel>
            </div>
          </div>
        )}
      </div>
    </CraftShell>
  );
}
